import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Typography,
  alpha,
} from '@mui/material';
import { ConfirmationNumber as PackageIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/currencyUtils';
import { formatCalendarDate } from '../../utils/dateFormatUtils';
import { getLocalizedValue } from '../../constants/Languages';
import CustomerPrimaryButton from '../customer/CustomerPrimaryButton';
import { brandAlpha } from '../../theme/brandTokens';
import {
  CUSTOMER_COLORS,
  customerSectionPaperSx,
} from '../../constants/customerTheme';

const SUCCESS_COLOR = '#2e7d32';

/**
 * One package on sale at the store, plus the customer's own copy of it when they hold one.
 */
const CustomerServicePackageCard = ({
  servicePackage,
  ownedPackage,
  serviceNames,
  currency,
  canBuy,
  showInStoreOnlyMessage,
  actionLoading,
  onBuy,
  onBook,
}) => {
  const { t, i18n } = useTranslation();
  const localizedName = getLocalizedValue(
    servicePackage.name,
    i18n.language,
    undefined,
    t('customer.packages.card.unnamed'),
  );
  const displayCurrency = servicePackage.currency || currency;
  const isOwned = Boolean(ownedPackage);
  const usedRatio = isOwned && ownedPackage.quantity > 0
    ? (ownedPackage.usedQuantity / ownedPackage.quantity) * 100
    : 0;

  return (
    <Card
      elevation={0}
      id={`customer-package-${servicePackage.id}`}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        ...customerSectionPaperSx,
        ...(isOwned ? { borderColor: alpha(SUCCESS_COLOR, 0.45), bgcolor: alpha(SUCCESS_COLOR, 0.03) } : {}),
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.12)',
          borderColor: isOwned ? alpha(SUCCESS_COLOR, 0.55) : brandAlpha('accent', 0.35),
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <PackageIcon sx={{ color: CUSTOMER_COLORS.primary, flexShrink: 0 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: CUSTOMER_COLORS.text }}>
              {localizedName}
            </Typography>
          </Box>
          {isOwned ? (
            <Chip
              label={t('customer.packages.card.owned')}
              size="small"
              sx={{ bgcolor: alpha(SUCCESS_COLOR, 0.14), color: SUCCESS_COLOR, fontWeight: 700, flexShrink: 0 }}
            />
          ) : null}
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 700, color: CUSTOMER_COLORS.text, mb: 1 }}>
          {formatCurrency(servicePackage.price, displayCurrency)}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
          <Chip
            label={t('customer.packages.card.quantity', { count: Number(servicePackage.quantity) || 0 })}
            size="small"
            sx={{ bgcolor: brandAlpha('accent', 0.14), color: CUSTOMER_COLORS.primaryMuted, fontWeight: 600 }}
          />
          <Chip
            label={servicePackage.expiresInMonths
              ? t('customer.packages.card.expiresInMonths', { count: Number(servicePackage.expiresInMonths) })
              : t('customer.packages.card.noExpiry')}
            size="small"
            variant="outlined"
            sx={{ borderColor: brandAlpha('accent', 0.35), color: CUSTOMER_COLORS.primaryMuted, fontWeight: 600 }}
          />
        </Box>

        {serviceNames ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('customer.packages.card.includes', { services: serviceNames })}
          </Typography>
        ) : null}

        {isOwned ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: CUSTOMER_COLORS.text, mb: 0.75 }}>
              {t('customer.packages.usage.remaining', {
                remaining: ownedPackage.remainingQuantity,
                total: ownedPackage.quantity,
              })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={usedRatio}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: alpha(SUCCESS_COLOR, 0.15),
                '& .MuiLinearProgress-bar': { bgcolor: SUCCESS_COLOR },
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              {ownedPackage.expiresOn
                ? t('customer.packages.usage.validUntil', { date: formatCalendarDate(ownedPackage.expiresOn) })
                : t('customer.packages.usage.noExpiry')}
            </Typography>
          </Box>
        ) : null}

        {showInStoreOnlyMessage ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 'auto', mb: 1 }}>
            {t('customer.packages.inStoreOnlyMessage')}
          </Typography>
        ) : null}

        <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
          {isOwned ? (
            <CustomerPrimaryButton
              fullWidth
              id={`customer-package-book-${servicePackage.id}`}
              disabled={actionLoading}
              onClick={() => onBook?.(ownedPackage)}
              sx={{ width: '100%', minWidth: 'unset' }}
            >
              {t('customer.packages.actions.bookWithPackage')}
            </CustomerPrimaryButton>
          ) : null}

          {!isOwned && canBuy ? (
            <CustomerPrimaryButton
              fullWidth
              id={`customer-package-buy-${servicePackage.id}`}
              disabled={actionLoading}
              onClick={() => onBuy?.(servicePackage)}
              startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ width: '100%', minWidth: 'unset' }}
            >
              {t('customer.packages.actions.buy')}
            </CustomerPrimaryButton>
          ) : null}

          {!isOwned && !canBuy && !showInStoreOnlyMessage ? (
            <Button disabled fullWidth variant="outlined" sx={{ textTransform: 'none' }}>
              {t('customer.packages.actions.unavailable')}
            </Button>
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
};

export default CustomerServicePackageCard;
